import React, {Component} from 'react';
import {Modal, Switch} from 'antd';
import {CheckOutlined, CloseOutlined} from '@ant-design/icons';
import CustomSelect from '../_Generic/Select/CustomSelect';
import CFDITypes from '../../Catalogs/tipoComprobante.json';
import CFDIComplements from '../../Catalogs/complements.json';
import EachDaysCatalog from '../../Catalogs/eachDaysCatalog.json';
import LapseDaysCatalog from '../../Catalogs/lapseDaysCatalog.json';
import RoleHelper from '../../Helpers/RoleHelper';

const ALL_SELECT_OPTION = {
    'label': 'Todos',
    'value': null,
    'disabled': false
};

/**
 * Modal para editar una descarga programada existente.
 * La llave (RFC, tipo de documentos, tipo de solicitud) no es editable;
 * solo los parámetros: frecuencia, rango, tipo, complemento, estado y switches.
 */
class EditScheduleModal extends Component {

    constructor(props) {
        super(props);
        this.state = this.buildStateFromRow(props.row);
    }

    componentDidUpdate(prevProps) {
        if (this.props.row !== prevProps.row && this.props.row) {
            this.setState(this.buildStateFromRow(this.props.row));
        }
    }

    buildStateFromRow(row) {
        if (!row) return {};
        return {
            frequencyInDays: String(row.frequencyInDays),
            lapseDays: String(row.lapseDays),
            cfdiType: row.cfdiType || null,
            cfdiComplement: row.cfdiComplement || null,
            cfdiStatus: row.cfdiStatus || 'Todos',
            generatePdf: row.generatePdf === 1,
            googleDrive: row.googleDrive === 1
        };
    }

    isCFDIStatusEditable = () => {
        const {row} = this.props;
        // El estatus (EstadoComprobante) solo se envía al SAT en XML (0 y 3) y Metadata (2).
        if (row.processType !== 0 && row.processType !== 2 && row.processType !== 3) return false;
        // En XML de Recepción el SAT solo permite vigentes.
        if ((row.processType === 0 || row.processType === 3) && row.documentType === 1) return false;
        return true;
    }

    handleOk = () => {
        const statusEditable = this.isCFDIStatusEditable();
        const changes = {
            frequencyInDays: parseInt(this.state.frequencyInDays, 10),
            lapseDays: parseInt(this.state.lapseDays, 10),
            // null en el select = 'Todos'; se envía '' para limpiar el filtro en BD
            cfdiType: this.state.cfdiType || '',
            cfdiComplement: this.state.cfdiComplement || '',
            cfdiStatus: statusEditable ? this.state.cfdiStatus : 'Vigente',
            generatePdf: this.state.generatePdf ? 1 : 0,
            googleDrive: this.state.googleDrive ? 1 : 0
        };
        this.props.onSave(changes);
    }

    selectDays = () => {
        return EachDaysCatalog.map(d => ({
            'label': d.label, 'key': d.key, 'value': d.key, 'disabled': false
        }));
    }

    selectLapseDays = () => {
        return LapseDaysCatalog.map(ct => ({
            'label': ct.label, 'key': ct.key, 'value': ct.key, 'disabled': false
        }));
    }

    selectCFDIStatus = () => {
        return [
            {label: 'Vigente', key: 'Vigente', value: 'Vigente', disabled: false},
            {label: 'Cancelado', key: 'Cancelado', value: 'Cancelado', disabled: false},
            {label: 'Todos', key: 'Todos', value: 'Todos', disabled: false}
        ];
    }

    selectCFDIType = () => {
        const {roleHelper, row} = this.props;
        const documentTypeDict = {0: 'emision', 1: 'recepcion'};
        const documentType = documentTypeDict[row.documentType];
        return [
            {
                ...ALL_SELECT_OPTION,
                disabled: !roleHelper.hasAllOptions(RoleHelper.ACTION_SCHEDULE, documentType)
            },
            ...CFDITypes.map(ct => ({
                'label': ct.label,
                'key': ct.key,
                'value': ct.key,
                'disabled': !roleHelper.hasAccess(RoleHelper.ACTION_SCHEDULE, documentType, ct.key)
            }))
        ];
    }

    selectCFDIComplement = () => {
        return [
            ALL_SELECT_OPTION,
            ...CFDIComplements.map(ct => ({
                'label': ct.label, 'key': ct.key, 'value': ct.key, 'disabled': false
            }))
        ];
    }

    documentParser(doc) {
        return doc === 1 ? 'Recepción' : 'Emisión';
    }

    requestParser(req) {
        switch (req) {
            case 0:
                return 'Descarga XML';
            case 1:
                return 'Portal del SAT';
            case 2:
                return 'Validación EFOS';
            case 3:
                return 'Descarga XML con fecha de cancelación';
            default:
                return 'N/A';
        }
    }

    render() {
        const {visible, row, onCancel, isFreePlan, gDriveDisabled} = this.props;
        if (!row) return null;
        const cfdiDisabled = row.processType === 1;
        const inputStyle = {width: '100%'};
        const fieldStyle = {marginBottom: '12px'};
        return (
            <Modal
                title='Editar descarga programada'
                visible={visible}
                onOk={this.handleOk}
                onCancel={onCancel}
                okText='Guardar'
                cancelText='Cancelar'
            >
                <p className='label'>
                    RFC <strong>{row.taxId.toUpperCase()}</strong> · {this.documentParser(row.documentType)} · {this.requestParser(row.processType)}
                </p>
                <div style={fieldStyle}>
                    <label className='label block'>Frecuencia:</label>
                    <CustomSelect
                        value={this.state.frequencyInDays}
                        style={inputStyle}
                        onChange={(value) => this.setState({frequencyInDays: value})}
                        items={this.selectDays()}
                    />
                </div>
                <div style={fieldStyle}>
                    <label className='label block'>Rango:</label>
                    <CustomSelect
                        value={this.state.lapseDays}
                        style={inputStyle}
                        onChange={(value) => this.setState({lapseDays: value})}
                        items={this.selectLapseDays()}
                    />
                </div>
                <div style={fieldStyle}>
                    {/* Estado del comprobante ante el SAT (Vigente/Cancelado/Todos),
                        NO el Activo/Inactivo de la configuración (switch del grid). */}
                    <label className='label block'>Estado CFDI:</label>
                    <CustomSelect
                        disabled={!this.isCFDIStatusEditable()}
                        value={this.state.cfdiStatus}
                        style={inputStyle}
                        onChange={(value) => this.setState({cfdiStatus: value})}
                        items={this.selectCFDIStatus()}
                    />
                </div>
                <div style={fieldStyle}>
                    <label className='label block'>Tipo:</label>
                    <CustomSelect
                        disabled={cfdiDisabled}
                        value={this.state.cfdiType}
                        style={inputStyle}
                        onChange={(value) => this.setState({cfdiType: value})}
                        items={this.selectCFDIType()}
                    />
                </div>
                <div style={fieldStyle}>
                    <label className='label block'>Complemento:</label>
                    <CustomSelect
                        disabled={cfdiDisabled}
                        value={this.state.cfdiComplement}
                        style={inputStyle}
                        onChange={(value) => this.setState({cfdiComplement: value})}
                        items={this.selectCFDIComplement()}
                    />
                </div>
                <div style={{display: 'flex', alignItems: 'center', marginBottom: '12px'}}>
                    <Switch
                        onChange={(value) => this.setState({generatePdf: value})}
                        checkedChildren={<CheckOutlined/>}
                        unCheckedChildren={<CloseOutlined/>}
                        checked={this.state.generatePdf}
                        disabled={isFreePlan}
                        style={{width: '50px'}}
                    />
                    <label className='label' style={{marginLeft: '8px'}}>Generar Facturas PDF</label>
                </div>
                <div style={{display: 'flex', alignItems: 'center'}}>
                    <Switch
                        onChange={(value) => this.setState({googleDrive: value})}
                        checkedChildren={<CheckOutlined/>}
                        unCheckedChildren={<CloseOutlined/>}
                        checked={this.state.googleDrive}
                        disabled={gDriveDisabled}
                        style={{width: '50px'}}
                    />
                    <label className='label' style={{marginLeft: '8px'}}>Google Drive</label>
                </div>
            </Modal>
        );
    }
}

export default EditScheduleModal;
